import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../../config';

const todayStr = () => new Date().toISOString().split('T')[0];
const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x.toISOString().split('T')[0];
};
const monthStart = () => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

export const PRESETS = {
    today: () => ({ start: todayStr(), end: todayStr() }),
    yesterday: () => ({ start: addDays(todayStr(), -1), end: addDays(todayStr(), -1) }),
    '7d': () => ({ start: addDays(todayStr(), -6), end: todayStr() }),
    '30d': () => ({ start: addDays(todayStr(), -29), end: todayStr() }),
    mtd: () => ({ start: monthStart(), end: todayStr() })
};

const safeJsonParse = (raw, fallback) => {
    if (!raw) return fallback;
    try { return JSON.parse(decodeURIComponent(escape(atob(raw)))); }
    catch { return fallback; }
};
const encode = (obj) => {
    try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }
    catch { return ''; }
};

const inflight = new Map();

export default function useDatagridQuery({ mode, endpoint, exportEndpoint, onSuccess }) {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const derived = useMemo(() => {
        const preset = searchParams.get('preset') || 'today';
        const fromPreset = PRESETS[preset] ? PRESETS[preset]() : { start: '', end: '' };
        return {
            preset,
            startDate: searchParams.get('start') || fromPreset.start,
            endDate: searchParams.get('end') || fromPreset.end,
            page: Math.max(1, parseInt(searchParams.get('page') || '1', 10)),
            pageSize: parseInt(searchParams.get('size') || '100', 10),
            filterModel: safeJsonParse(searchParams.get('filter'), {}),
            sortModel: safeJsonParse(searchParams.get('sort'), [])
        };
    }, [searchParams]);

    const { preset, startDate, endDate, page, pageSize, filterModel, sortModel } = derived;

    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    const filterModelRef = useRef(filterModel);
    const sortModelRef = useRef(sortModel);
    filterModelRef.current = filterModel;
    sortModelRef.current = sortModel;

    const concessionaire_id = localStorage.getItem('concessionaireId');
    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const writeParams = useCallback((patch) => {
        const next = new URLSearchParams(searchParams);
        const setOrDel = (k, v) => {
            if (v === undefined || v === null || v === '' || (typeof v === 'object' && Object.keys(v).length === 0)) {
                next.delete(k);
            } else if (typeof v === 'object') {
                next.set(k, encode(v));
            } else {
                next.set(k, String(v));
            }
        };
        Object.entries(patch).forEach(([k, v]) => setOrDel(k, v));
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const insertToLog = useCallback((task) => {
        if (!login_id) return;
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id,
            module: 'Data Grid',
            task: (login_name || '') + task
        }, { headers }).catch(() => { });
    }, [login_id, login_name, concessionaire_id, headers]);

    const reqKey = useMemo(() => JSON.stringify({
        endpoint, concessionaire_id, startDate, endDate, page, pageSize, filterModel, sortModel
    }), [endpoint, concessionaire_id, startDate, endDate, page, pageSize, filterModel, sortModel]);

    const doFetch = useCallback(async (pageIndex, silent) => {
        if (!startDate || !endDate || endDate < startDate) return;
        const key = JSON.stringify({
            endpoint, concessionaire_id, startDate, endDate, page: pageIndex, pageSize, filterModel, sortModel
        });
        setLoading(true);
        try {
            let promise = inflight.get(key);
            if (!promise) {
                const skip = (pageIndex - 1) * pageSize;
                const firstSort = (sortModel && sortModel[0]) || {};
                const payload = {
                    concessionaire_id,
                    property_access: '',
                    start_date: startDate,
                    end_date: endDate,
                    skip,
                    limit: pageSize,
                    filterModel: filterModel || {},
                    sortField: firstSort.colId || undefined,
                    sortDir: firstSort.sort || undefined,
                    sortModel: sortModel || []
                };
                promise = axios.post(`${config.hostname}${endpoint}`, payload, { headers })
                    .finally(() => { inflight.delete(key); });
                inflight.set(key, promise);
            }
            const res = await promise;
            const gridRows = Array.isArray(res.data?.data) ? res.data.data : [];
            const serverTotal = res.data?.totalCount;
            if (serverTotal == null) {
                console.warn('[Datagrid] server omitted totalCount; pagination will be approximate');
            }
            setRows(gridRows);
            setTotalCount(Number(serverTotal ?? gridRows.length ?? 0));
            setHasLoaded(true);
            if (!silent) insertToLog(' viewed data grid');
            onSuccess?.(gridRows);
        } catch (e) {
            console.error('[Datagrid] fetch error:', e?.message);
            setRows([]);
            setTotalCount(0);
            toast.error(t('Failed to load data'));
        } finally {
            setLoading(false);
        }
    }, [endpoint, concessionaire_id, headers, startDate, endDate, pageSize, filterModel, sortModel, insertToLog, onSuccess, t]);

    const fetchPage = useCallback((pageIndex = page, opts = {}) => {
        if (!startDate || !endDate) {
            toast.warning(t('Select start date and end date'));
            return;
        }
        if (endDate < startDate) {
            toast.warning(t('End date cannot be before start date'));
            return;
        }
        if (pageIndex !== page) writeParams({ page: pageIndex });
        doFetch(pageIndex, !!opts.silent);
    }, [startDate, endDate, page, writeParams, doFetch, t]);

    const applyPreset = useCallback((name) => {
        if (!PRESETS[name]) {
            writeParams({
                preset: 'custom',
                start: startDate || todayStr(),
                end: endDate || todayStr(),
                page: 1
            });
            return;
        }
        const { start, end } = PRESETS[name]();
        writeParams({ preset: name, start, end, page: 1 });
    }, [writeParams, startDate, endDate]);

    const setCustomDates = useCallback((start, end) => {
        writeParams({ preset: 'custom', start, end, page: 1 });
    }, [writeParams]);

    const setFilter = useCallback((fm) => {
        writeParams({ filter: fm || {}, page: 1 });
    }, [writeParams]);

    const setSort = useCallback((sm) => {
        writeParams({ sort: sm || [], page: 1 });
    }, [writeParams]);

    const changePageSize = useCallback((size) => {
        writeParams({ size, page: 1 });
    }, [writeParams]);

    const reset = useCallback(() => {
        setRows([]);
        setTotalCount(0);
        setHasLoaded(false);
        setSearchParams(new URLSearchParams(mode ? { mode } : {}), { replace: true });
        toast.info(t('Filters reset'));
    }, [setSearchParams, mode, t]);

    const exportCsv = useCallback(async () => {
        if (!startDate || !endDate) { toast.warn(t('Select both dates first')); return; }
        if (endDate < startDate) { toast.warn(t('Fix the date range')); return; }
        setExporting(true);
        try {
            const firstSort = (sortModel && sortModel[0]) || {};
            const payload = {
                concessionaire_id,
                property_access: '',
                start_date: startDate,
                end_date: endDate,
                filterModel: filterModel || {},
                sortField: firstSort.colId || undefined,
                sortDir: firstSort.sort || undefined,
                sortModel: sortModel || []
            };
            const res = await axios.post(`${config.hostname}${exportEndpoint}`, payload, {
                headers, responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `data_grid_${mode}_${startDate}_${endDate}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            insertToLog(' exported data grid CSV');
        } catch (e) {
            console.error(e);
            toast.error(t('Export failed'));
        } finally {
            setExporting(false);
        }
    }, [startDate, endDate, exportEndpoint, headers, concessionaire_id, mode, insertToLog, filterModel, sortModel, t]);

    useEffect(() => {
        if (startDate && endDate) {
            doFetch(page, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reqKey]);

    return {
        startDate, endDate, preset, page, pageSize, rows, totalCount, loading, exporting, hasLoaded,
        filterModelRef, sortModelRef,
        fetchPage, applyPreset, setCustomDates, setFilter, setSort, changePageSize, reset, exportCsv
    };
}
